<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Button } from "@/components/ui/button/index.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card/index.js";
import { Skeleton } from '@/components/ui/skeleton'
import { ChevronRight, Plus } from "lucide-vue-next";
import { GetOrganisations } from "@/services/organisationService.js";
import ViewModePicker from "@/components/ViewModePicker.vue";
import { getViewMode } from "@/services/viewModeService.js";

const router = useRouter();

let isLoading = ref(true);
let organisations = ref([]);
let hasOrganisations = computed(() => organisations.value.length > 0);
let viewMode = ref(getViewMode());

onMounted(async () => {
  organisations.value = await GetOrganisations();

  isLoading.value = false;
});

function gotoCreate() {
  router.push({ path: '/create' })
}

function goToOrganisation(organisation) {
  router.push({ path: `/${organisation.id}` });
}

function handleViewModeChange(mode) {
  viewMode.value = mode;
}

</script>

<template>
  <div class="container">
    <div class="flex-1 space-y-4">
      <div class="flex items-center justify-between space-y-2">
        <h2 class="text-xl">
          Your companies
        </h2>
      </div>

      <!-- Toolbar -->
      <div class="flex items-center justify-between space-y-2 bg-gray-100 p-4 rounded-md">
        <div class="flex items-center space-x-2">
          <Button @click="gotoCreate" v-if="!isLoading">
            <Plus />
            New company
          </Button>
          <Skeleton v-if="isLoading" class="w-24 h-8" />
        </div>
        <ViewModePicker v-model="viewMode" @update:model-value="handleViewModeChange" />
      </div>

      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4" v-if="isLoading">
        <div v-for="index in 3" :key="index" class="w-full">
          <Card>
            <CardHeader>
              <CardTitle>
                <Skeleton class="h-16" />
              </CardTitle>
              <CardDescription>
                <Skeleton class="h-4" />
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Skeleton class="h-4 w-24 mt-2" />
            </CardContent>
          </Card>
        </div>
      </div>

      <div v-if="!isLoading && viewMode === 'grid'" class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div v-for="organisation in organisations" :key="organisation.id" class="w-full" v-if="hasOrganisations">
          <Card>
            <CardHeader>
              <CardTitle>{{ organisation.name }}</CardTitle>
            </CardHeader>
            <CardContent>
              <Button @click="goToOrganisation(organisation)">
                Open
                <ChevronRight />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <div v-if="!isLoading && viewMode === 'list'" class="space-y-2">
        <Card v-for="organisation in organisations" :key="organisation.id" v-if="hasOrganisations">
          <CardContent class="p-4" @click="goToOrganisation(organisation)">
            <div class="flex items-center justify-between">
              <div class="space-y-1">
                <h3 class="font-semibold">
                  {{ organisation.name }}
                </h3>
              </div>
              <Button @click="goToOrganisation(organisation)" variant="ghost" size="sm">
                Open
                <ChevronRight />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div v-if="!hasOrganisations && !isLoading">
        <Card class="w-1/2 mx-auto">
          <CardHeader>
            <CardTitle class="text-center">It looks like you don't have any companies yet</CardTitle>
            <CardDescription class="text-center mt-8">Getting started with PanelPop is easy</CardDescription>
          </CardHeader>
          <CardContent class="text-center">
            <Button @click="gotoCreate">
              <Plus />
              Create one now
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
</template>

<style scoped></style>